// src/components/Admin/Campaigns/builder/ai/AiOutlineButton.tsx
//
// A one-click "draft the whole campaign outline with AI" affordance. It arms the
// background outline flow, shows a spinner while it runs, and previews the
// proposed campaign (name / description / duration + nested step tree) in a MUI
// dialog. It only ever PROPOSES: the parent decides how to persist via onAccept.
// If the model returns null it degrades to a small non-blocking note and closes.

import React from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { GhostButton, PrimaryButton } from '../primitives';
import { useCampaignOutlineDraft } from '../../ai/useCampaignAi';
import { CampaignOutlineDraft, StepStub } from '../../ai/campaignFlows';
import { stepKindLabel, trackLabel } from '../../../../../support/campaigns';
import styles from './guide.module.css';

function StubNodes(props: { stubs: StepStub[] }): JSX.Element {
  return (
    <ul className={styles.outlineTree}>
      {props.stubs.map((stub, i) => (
        <li className={styles.outlineNode} key={`${stub.name}-${i}`}>
          <div>
            <span className={styles.outlineNodeName}>{stub.name}</span>
            {(stub.track || stub.step_kind) && (
              <span className={styles.outlineNodeMeta}>
                {[stub.track && trackLabel(stub.track), stub.step_kind && stepKindLabel(stub.step_kind)]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </div>
          {stub.description && (
            <p className={styles.outlineNodeDesc}>{stub.description}</p>
          )}
          {stub.children && stub.children.length > 0 && (
            <StubNodes stubs={stub.children} />
          )}
        </li>
      ))}
    </ul>
  );
}

export function AiOutlineButton(props: {
  name: string;
  description?: string;
  disabled?: boolean;
  onAccept: (draft: CampaignOutlineDraft) => void;
}): JSX.Element {
  const { name, description, disabled, onAccept } = props;

  const [armed, setArmed] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  // Guards against acting on the brief window after enabling but before the
  // query actually starts fetching (data still null, isLoading not yet true).
  const sawLoadingRef = React.useRef(false);

  const { data, isLoading, refresh } = useCampaignOutlineDraft({
    name,
    description,
    enabled: armed,
  });

  React.useEffect(() => {
    if (!armed) {
      sawLoadingRef.current = false;
      return;
    }
    if (isLoading) {
      sawLoadingRef.current = true;
      return;
    }
    if (!sawLoadingRef.current) return; // fetch hasn't begun yet

    if (data) {
      setOpen(true);
      setFailed(false);
    } else {
      setFailed(true);
    }
  }, [armed, isLoading, data]);

  const reset = React.useCallback(() => {
    setArmed(false);
    setOpen(false);
    sawLoadingRef.current = false;
  }, []);

  const start = React.useCallback(() => {
    setFailed(false);
    if (armed) {
      refresh();
    } else {
      setArmed(true);
    }
  }, [armed, refresh]);

  const regenerate = React.useCallback(() => {
    setFailed(false);
    setOpen(false);
    sawLoadingRef.current = false;
    refresh();
  }, [refresh]);

  const accept = React.useCallback(() => {
    if (data) onAccept(data);
    reset();
  }, [data, onAccept, reset]);

  const loading = armed && isLoading;

  return (
    <>
      <PrimaryButton onClick={start} disabled={disabled || loading}>
        {loading ? (
          <span className={styles.spinRow}>
            <CircularProgress size={14} color="inherit" />
            Generating outline…
          </span>
        ) : (
          'Generate outline with AI'
        )}
      </PrimaryButton>

      {failed && !loading && (
        <p className={styles.outlineMeta} role="status">
          Couldn&apos;t generate — you can build manually.
        </p>
      )}

      <Dialog open={open && !!data} onClose={reset} maxWidth="sm" fullWidth>
        <DialogTitle>Proposed campaign outline</DialogTitle>
        <DialogContent dividers>
          {data && (
            <>
              <p className={styles.outlineName}>{data.name}</p>
              {data.description && (
                <p className={styles.outlineNodeDesc}>{data.description}</p>
              )}
              {data.estimated_duration_minutes != null && (
                <p className={styles.outlineMeta}>
                  Estimated duration: {data.estimated_duration_minutes} min
                </p>
              )}
              {data.steps.length > 0 ? (
                <StubNodes stubs={data.steps} />
              ) : (
                <p className={styles.outlineMeta}>No steps were proposed.</p>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <GhostButton onClick={regenerate}>Regenerate</GhostButton>
          <GhostButton onClick={reset}>Discard</GhostButton>
          <PrimaryButton onClick={accept} disabled={!data}>
            Use this outline
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AiOutlineButton;
